import { useCallback, useEffect, useMemo } from 'react';

import { useParams } from 'react-router';

import { useUpdateDashboard } from '@/features/dashboard/api';
import { useDrawerSteps } from '@/features/dashboard/components/Drawer/context/DrawerStepsContext';
import { HIGHLIGHTED_PRICE_MAX_ITEMS } from '@/features/dashboard/constants/const';
import { TAB_DEFAULT_BASE_NAME } from '@/features/dashboard/constants/names';
import { useBuildDashboardStructure } from '@/features/dashboard/hooks/sync/useBuildDashboardStructure';
import { useCurrentDashboardConfig } from '@/features/dashboard/hooks/useCurrentDashboardConfig';
import { useInitialDashboardName } from '@/features/dashboard/hooks/useInitialDashboardName';
import { getTabContent } from '@/features/dashboard/lib/transformers';
import { useDashboardEditActions } from '@/features/dashboard/pages/Edit/hooks/useDashboardEditActions';
import { selectCurrentTabId } from '@/features/dashboard/state/dashboardEdit.selectors';
import type { MarketApp } from '@/features/dashboard/types';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';
import { useAppSelector } from '@/store/hooks';

import { handleEditModeSave, wouldExceedTilesLimit } from './appWidgetActions';

export const useSaveApp = () => {
  const { tabName } = useParams<{ tabName?: string }>();
  const initialDashboardName = useInitialDashboardName();
  const currentTabId = useAppSelector(selectCurrentTabId);
  const { closeDrawer, isEditMode, editingTarget, setEditingTarget } = useDrawerSteps();
  const actions = useDashboardEditActions();
  const allDashboardTabs = useCurrentDashboardConfig();
  const { showErrorSnackbar } = useSnackbarNotifications();

  const currentTabName = tabName || Object.keys(allDashboardTabs || {})[0] || TAB_DEFAULT_BASE_NAME;

  const buildDashboardStructure = useBuildDashboardStructure();

  const { mutate, isSuccess, isPending } = useUpdateDashboard(
    initialDashboardName,
    'New app added!',
    currentTabName
  );

  const currentTilesCount = useMemo(() => {
    if (!allDashboardTabs) return 0;
    const tabValue = allDashboardTabs[currentTabName];
    if (!tabValue) return 0;
    const tabContent = getTabContent(tabValue);
    const tilesWidget = tabContent.find(app => 'type' in app);
    return tilesWidget && 'tiles' in tilesWidget ? tilesWidget.tiles.length : 0;
  }, [allDashboardTabs, currentTabName]);

  useEffect(() => {
    if (isSuccess) {
      closeDrawer();
    }
  }, [isSuccess, closeDrawer]);

  const handleSave = useCallback(
    (widget: MarketApp | undefined) => {
      if (!widget) return;
      if (isEditMode && !currentTabId) return;

      if (isEditMode) {
        handleEditModeSave({
          actions,
          widget,
          currentTabId,
          editingTarget,
          setEditingTarget,
          closeDrawer
        });
        return;
      }

      if (wouldExceedTilesLimit(widget, currentTilesCount)) {
        showErrorSnackbar(
          `Maximum ${HIGHLIGHTED_PRICE_MAX_ITEMS} highlighted prices allowed per tab.`
        );
        return;
      }

      const structurePayload = buildDashboardStructure(widget);
      mutate(structurePayload);
    },
    [
      actions,
      buildDashboardStructure,
      closeDrawer,
      currentTabId,
      currentTilesCount,
      editingTarget,
      isEditMode,
      mutate,
      setEditingTarget,
      showErrorSnackbar
    ]
  );

  return { handleSave, isPending };
};
